import type { ChatActivity } from '../api/activity'

type NumericSample = { timestampMs: number; messagesPerSecond: number }
type PlotPoint = NumericSample & { x: number; y: number }

function reduceSegment(samples: NumericSample[], from: number, to: number, width: number): NumericSample[] {
  const reduced: NumericSample[] = []
  let bucket = -1
  let first = samples[0]
  let last = first
  let minimum = first
  let maximum = first
  const flush = () => {
    const candidates = new Map([first, minimum, maximum, last].map((sample) => [sample.timestampMs, sample]))
    reduced.push(...[...candidates.values()].sort((a, b) => a.timestampMs - b.timestampMs))
  }
  for (const sample of samples) {
    const nextBucket = Math.min(width - 1, Math.floor((sample.timestampMs - from) / (to - from) * width))
    if (nextBucket !== bucket) {
      if (bucket >= 0) flush()
      bucket = nextBucket
      first = minimum = maximum = sample
    }
    last = sample
    if (sample.messagesPerSecond < minimum.messagesPerSecond) minimum = sample
    if (sample.messagesPerSecond > maximum.messagesPerSecond) maximum = sample
  }
  flush()
  return reduced
}

/** Reduce each uninterrupted run separately, retaining extrema in each pixel column. */
export function buildActivityPlot(
  data: Pick<ChatActivity, 'from' | 'to' | 'samples' | 'sampleIntervalMs'>,
  requestedWidth: number,
  height = 100,
) {
  const width = Math.max(1, Math.floor(requestedWidth))
  const baseline = height - 8
  const top = 8
  const runs: NumericSample[][] = []
  let run: NumericSample[] = []
  let peakRate = 0
  const finishRun = () => {
    if (run.length) runs.push(run)
    run = []
  }
  for (const sample of data.samples) {
    if (sample.timestampMs < data.from || sample.timestampMs >= data.to) continue
    const value = sample.messagesPerSecond
    if (value === null || !Number.isFinite(value) || value < 0) {
      finishRun()
      continue
    }
    const previous = run.at(-1)
    if (previous && sample.timestampMs - previous.timestampMs !== data.sampleIntervalMs) finishRun()
    run.push({ timestampMs: sample.timestampMs, messagesPerSecond: value })
    peakRate = Math.max(peakRate, value)
  }
  finishRun()

  const magnitude = peakRate > 0 ? 10 ** Math.floor(Math.log10(peakRate)) : 1
  const normalizedPeak = peakRate / magnitude
  const scaleMax = (normalizedPeak <= 1 ? 1 : normalizedPeak <= 2 ? 2 : normalizedPeak <= 5 ? 5 : 10) * magnitude
  const project = (sample: NumericSample): PlotPoint => ({
    ...sample,
    x: (sample.timestampMs - data.from) / (data.to - data.from) * width,
    y: baseline - sample.messagesPerSecond / scaleMax * (baseline - top),
  })
  const segments = runs.map((samples) => {
    const reduced = reduceSegment(samples, data.from, data.to, width)
    const last = samples.at(-1)!
    // Carry the final value through its available sample slot. This makes a
    // lone sample/zero visible without connecting across an unavailable minute.
    const points = [...reduced.map(project), project({
      timestampMs: Math.min(data.to, last.timestampMs + data.sampleIntervalMs),
      messagesPerSecond: last.messagesPerSecond,
    })]
    const coordinate = (point: PlotPoint) => `${Number(point.x.toFixed(3))},${Number(point.y.toFixed(3))}`
    const line = `M${points.map(coordinate).join(' L')}`
    const area = `${line} L${Number(points.at(-1)!.x.toFixed(3))},${baseline} L${Number(points[0].x.toFixed(3))},${baseline} Z`
    return { points, line, area }
  })
  return { width, height, baseline, peakRate, scaleMax, segments }
}
